import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts

import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import PermissionGuard from '../guards/PermissionGuard';
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          )
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          )
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'verify', element: <VerifyCode /> }
      ]
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { path: '/', element: <Navigate to="/dashboard/sefi/aianalytics" replace /> },
        { path: 'maindashboard', element: <MainDashboard />},
        { path: 'customermanagement', element: <PermissionGuard permissionId={131}><CustomerManagement /></PermissionGuard>},
        { path: 'activitylog', element: <ActivityLog />},
        { path: 'aianalytics', element: <Navigate to="/dashboard/sefi/aianalytics" replace /> },
        {
          path: 'sefi',
          children: [
            { path: '/', element: <Navigate to="/dashboard/sefi/aianalytics" replace /> },
            { path: 'aianalytics', element: <AIAnalytics /> },
            { path: 'sefisettings', element: <SefiSettings /> }
          ]
        },
        {
          path: 'pointofsale',
          children: [
            { path: '/', element: <Navigate to="/dashboard/pointofsale/customerlookup" replace /> },
            { path: 'customerlookup', element: <PermissionGuard permissionId={131}><CustomerLookup /></PermissionGuard> },
            { path: 'allorders', element: <PermissionGuard permissionId={175}><AllOrders /></PermissionGuard> },
            { path: 'commissionagents', element: <PermissionGuard permissionId={181}><CommissionAgents /></PermissionGuard> },
            { path: 'reversetaxcalculator', element: <ReverseTaxCalculator />},
            { path: 'printersettings', element: <PermissionGuard permissionId={174}><PrinterSettings /></PermissionGuard>},
            { path: 'poscart', element: <PointOfSaleSystem />},
            { path: 'placeorder', element: <PointOfSaleSystem />}
          ]
        },
        {
          path: 'expenses',
          children: [
            { path: '/', element: <Navigate to="/dashboard/expenses/allexpenses" replace /> },
            { path: 'allexpenses', element: <PermissionGuard permissionId={173}><AllExpenses /></PermissionGuard> },
            { path: 'expensetype', element: <PermissionGuard permissionId={179}><ExpenseType /></PermissionGuard> },
          ]
        },
        {
          path: 'inventory',
          children: [
            { path: '/', element: <Navigate to="/dashboard/inventory/inventorylist" replace /> },
            { path: 'inventorylist', element: <PermissionGuard permissionId={34}><Inventory /></PermissionGuard> },
            { path: 'inventorylist/edit/:id', element: <PermissionGuard permissionId={50}><InventoryEdit /></PermissionGuard> },
            { path: 'inventoryaudit', element: <PermissionGuard permissionId={160}><InventoryAudit /></PermissionGuard> },
            { path: 'categorytype', element: <CategoryType />},
            { path: 'datavariable', element: <DataVariable />},
            { path: 'productcategory', element: <PermissionGuard permissionId={34}><ProductCategory /></PermissionGuard>},
            { path: 'taxcategory', element: <PermissionGuard permissionId={34}><TaxCategory /></PermissionGuard>},
            { path: 'printbarcode', element: <PermissionGuard permissionId={38}><PrintBarcode /></PermissionGuard>},
            { path: 'alloutgoingtransfers', element: <AllOutgoingTransfers />},
            { path: 'allincomingtransfers', element: <AllIncomingTransfers />},
            { path: 'heldtransferorders', element: <HeldTransferOrders />},
            { path: 'transfers', element: <Transfers />},
          ]
        },
        {
          path: 'purchase',
          children: [
            { path: '/', element: <Navigate to="/dashboard/purchase/allsuppliers" replace /> },
            { path: 'allsuppliers', element: <PermissionGuard permissionId={81}><AllSuppliers /></PermissionGuard> },
            { path: 'allsuppliertypes', element: <PermissionGuard permissionId={180}><AllSupplierTypes /></PermissionGuard> },
            { path: 'receiveinventoryorders', element: <PermissionGuard permissionId={134}><ReceiveInventoryOrders /></PermissionGuard> }
          ]
        },
        {
          path: 'staffmanagement',
          children: [
            { path: '/', element: <Navigate to="/dashboard/staffmanagement/staffdirectory" replace /> },
            { path: 'staffdirectory', element: <PermissionGuard permissionId={98}><StaffDirectory /></PermissionGuard> },
            { path: 'userroles', element: <PermissionGuard permissionId={177}><UserRoles /></PermissionGuard> },
            { path: 'staffscheduler', element: <StaffScheduler />},
            { path: 'stafftimetracking', element: <StaffTimeTracking />},
            { path: 'stafftime/:id', element: <StaffTime />},
            { path: 'stafftime/:id/list', element: <StaffTimeList />}
          ]
        },
        {
          path: 'reports',
          children: [
            { path: '/', element: <Navigate to="/dashboard/reports/profitloss" replace /> },
            { path: 'profitloss', element: <ProfitLoss /> },
            { path: 'customers', element: <PermissionGuard permissionId={157}><Customers /></PermissionGuard> },
            { path: 'customeroutstandingreports', element: <PermissionGuard permissionId={157}><CustomerOutstandingReports /></PermissionGuard> },
            { path: 'staffs', element: <PermissionGuard permissionId={128}><Staffs /></PermissionGuard>},
            { path: 'expenses', element: <Expenses />},
            { path: 'products', element: <PermissionGuard permissionId={158}><Products /></PermissionGuard>},
            { path: 'receiveinventory', element: <ReceiveInventory />},
            { path: 'sales', element: <PermissionGuard permissionId={155}><Sales /></PermissionGuard>},
            { path: '_salesbyfilter', element: <SalesByFilter />},
            { path: 'refundedsales', element: <RefundedSales />},
            { path: '_salesbycategory', element: <SalesByCategory />},
            { path: '_salescounts', element: <SalesCounts />},
            { path: '_salescountbyhour', element: <SalesCountByHour />},
            { path: '_salescountbyday', element: <SalesCountByDay /> },
            { path: '__salescountbydayandhour', element: <SalesCountByDayAndHour /> },
            { path: 'bestseller', element: <BestSeller />},
            { path: '_bestsellersbyquantity', element: <BestSellersByQuantity /> },
            { path: 'worstsellers', element: <WorstSellers /> },
            { path: '_worstsellersbyquantity', element: <WorstSellersByQuantity /> },
            { path: '_payments', element: <Payments />},
            { path: '_customerpendingpayments', element: <CustomerPendingPayments />},
          ]
        },
        {
          path: 'user',
          children: [
            { path: '/', element: <Navigate to="/dashboard/user/account" replace /> },
            { path: 'profile', element: <UserProfile /> },
            { path: 'account', element: <UserAccount /> }
          ]
        }
      ]
    },

    // Docs Routes
    

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '500', element: <Page500 /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> }
      ]
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        { path: '/', element: <Login /> },
      ]
    },
    { path: '*', element: <Navigate to="/404" replace /> }
  ]);
}

// IMPORT COMPONENTS

// Authentication
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const Register = Loadable(lazy(() => import('../pages/authentication/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/authentication/ResetPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));
// Dashboard
const PointOfSaleSystem = Loadable(lazy(() => import('../components/_dashboard/CustomerLookup/PointOfSaleSystem')));
const AllOutgoingTransfers = Loadable(lazy (() => import('../pages/dashboard/AllOutgoingTransfers')));
const AllIncomingTransfers = Loadable(lazy (() => import('../pages/dashboard/AllIncomingTransfers')));
const HeldTransferOrders = Loadable(lazy (() => import('../pages/dashboard/HeldTransferOrders')));
const Transfers = Loadable(lazy (() => import('../pages/dashboard/Transfers')));
const PrinterSettings = Loadable(lazy (() => import('../pages/dashboard/PrinterSettings')));
const ReverseTaxCalculator = Loadable(lazy (() => import('../pages/dashboard/ReverseTaxCalculator')));
const AllOrders = Loadable(lazy (() => import('../pages/dashboard/AllOrders')));
const CommissionAgents = Loadable(lazy(() => import('../pages/dashboard/CommissionAgents')));
const CustomerLookup = Loadable(lazy (() => import('../pages/dashboard/CustomerLookup')));
const CustomerPendingPayments = Loadable(lazy (() => import('../pages/dashboard/CustomerPendingPayments')));
const Payments = Loadable(lazy (() => import('../pages/dashboard/Payments')));
const WorstSellersByQuantity = Loadable(lazy (() => import('../pages/dashboard/WorstSellersByQuantity')));
const WorstSellers = Loadable(lazy (() => import('../pages/dashboard/WorstSellers')));
const BestSellersByQuantity = Loadable(lazy (() => import('../pages/dashboard/BestSellersByQuantity')));
const BestSeller = Loadable(lazy (() => import('../pages/dashboard/BestSeller')));
const SalesCountByDayAndHour = Loadable(lazy (() => import('../pages/dashboard/SalesCountByDayAndHour')));
const SalesCountByDay = Loadable(lazy (() => import('../pages/dashboard/SalesCountByDay')));
const SalesCountByHour = Loadable(lazy (() => import('../pages/dashboard/SalesCountByHour')));
const SalesCounts = Loadable(lazy (() => import ('../pages/dashboard/SalesCounts')));
const SalesByCategory = Loadable(lazy (() => import ('../pages/dashboard/SalesByCategory')));
const RefundedSales = Loadable(lazy (() => import ('../pages/dashboard/RefundedSales')));
const SalesByFilter = Loadable(lazy(() => import ('../pages/dashboard/SalesByFilter')));
const Sales = Loadable(lazy(() => import ('../pages/dashboard/Sales')));
const ReceiveInventory = Loadable(lazy(() => import ('../pages/dashboard/ReceiveInventory')));
const Products = Loadable(lazy(() => import ('../pages/dashboard/Products')));
const Expenses = Loadable(lazy(() => import ('../pages/dashboard/Expenses')));
const Staffs = Loadable(lazy(() => import ('../pages/dashboard/Staffs')));
const Customers = Loadable(lazy(() => import ('../pages/dashboard/Customers')));
const CustomerOutstandingReports = Loadable(lazy(() => import ('../pages/dashboard/CustomerOutstandingReports')));
const ProfitLoss = Loadable(lazy(() => import ('../pages/dashboard/ProfitLoss')));
const ActivityLog = Loadable(lazy(() => import ('../pages/dashboard/ActivityLog')));
const StaffDirectory = Loadable(lazy(() => import ('../pages/dashboard/StaffDirectory')));
const UserRoles = Loadable(lazy(() => import ('../pages/dashboard/UserRoles')));
const StaffScheduler = Loadable(lazy(() => import ('../pages/dashboard/StaffScheduler')));
const StaffTimeTracking = Loadable(lazy(() => import ('../pages/dashboard/StaffTimeTracking')));
const StaffTime = Loadable(lazy(() => import ('../pages/dashboard/StaffTime')));
const StaffTimeList = Loadable(lazy(() => import('../pages/dashboard/StaffTimeList')));
const ReceiveInventoryOrders = Loadable(lazy(() => import ('../pages/dashboard/ReceiveInventoryOrders')));
const AllSupplierTypes = Loadable(lazy(() => import ('../pages/dashboard/AllSupplierTypes'))); 
const AllSuppliers = Loadable(lazy(() => import ('../pages/dashboard/AllSuppliers')));
const PrintBarcode = Loadable(lazy(() => import('../pages/dashboard/PrintBarcode')));
const TaxCategory = Loadable(lazy(() => import('../pages/dashboard/TaxCategory')));
const ProductCategory = Loadable(lazy(() => import('../pages/dashboard/ProductCategory')));
const DataVariable = Loadable(lazy(() => import('../pages/dashboard/DataVariable')));
const CategoryType = Loadable(lazy(() => import('../pages/dashboard/CategoryType')));
const InventoryAudit = Loadable(lazy(() => import('../pages/dashboard/InventoryAudit')));
const InventoryEdit = Loadable(lazy(() => import('../pages/dashboard/InventoryEdit')));
const Inventory = Loadable(lazy(() => import('../pages/dashboard/Inventory')));
const AllExpenses = Loadable(lazy(() => import('../pages/dashboard/AllExpenses')));
const ExpenseType = Loadable(lazy(() => import('../pages/dashboard/ExpenseType')));
const CustomerManagement = Loadable(lazy(() => import('../pages/dashboard/CustomerManagement')));
const MainDashboard = Loadable(lazy(() => import('../pages/dashboard/MainDashboard')));
const AIAnalytics = Loadable(lazy(() => import('../pages/dashboard/AIAnalytics')));
const SefiSettings = Loadable(lazy(() => import('../pages/dashboard/SefiSettings')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
// Main
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
// Components
